/**
 * Structured-presentation contract for the Ticlick assistant panel.
 *
 * The assistant emits a CUSTOM AG-UI event named `presentation` whose `value`
 * is a `PresentationBlock` plus a correlating `messageId` (the id of the
 * assistant text message the block belongs to). These map to native CRM
 * components (tables, detail cards, metric tiles, timelines) instead of being
 * rendered as markdown.
 *
 * Field names are camelCase on the wire (e.g. `omittedCount`), matching the
 * rest of the AG-UI envelope. The guard below mirrors the hand-written
 * `isAguiEvent` style in `agui.types.ts`: an exact `kind` switch with per-kind
 * field checks and rejection of any unknown kind. Extra fields (such as the
 * `messageId` carried on the wire value) are tolerated — only presentation
 * blocks are validated here, not the tool events' no-args invariant.
 */

export type PresentationCellType = 'text' | 'badge' | 'date';

export type PresentationColumn = {
  key: string;
  label: string;
  type?: PresentationCellType;
};

export type PresentationRow = { id?: string | number; [k: string]: unknown };

export type TablePresentation = {
  kind: 'table';
  entity: string;
  title: string;
  columns: PresentationColumn[];
  rows: PresentationRow[];
  omittedCount?: number;
};

export type DetailField = {
  key: string;
  label: string;
  value: unknown;
  type?: PresentationCellType;
};

export type DetailCardPresentation = {
  kind: 'detail_card';
  entity: string;
  title: string;
  id?: string | number;
  fields: DetailField[];
};

export type Metric = {
  label: string;
  value: string | number;
  unit?: string;
  delta?: string | number;
};

export type MetricCardsPresentation = {
  kind: 'metric_cards';
  entity: string;
  title: string;
  metrics: Metric[];
};

export type TimelineItem = {
  id?: string | number;
  title: string;
  timestamp?: string;
  description?: string;
};

export type TimelinePresentation = {
  kind: 'timeline';
  entity: string;
  title: string;
  items: TimelineItem[];
};

export type EmptyStatePresentation = {
  kind: 'empty_state';
  entity: string;
  title: string;
  message: string;
};

export type PresentationBlock =
  | TablePresentation
  | DetailCardPresentation
  | MetricCardsPresentation
  | TimelinePresentation
  | EmptyStatePresentation;

/**
 * The value carried by the CUSTOM `presentation` event: a block plus the id of
 * the assistant message it correlates to.
 */
export type PresentationEventValue = PresentationBlock & { messageId: string };

// ── Runtime type guard ───────────────────────────────────────────────────────

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Every element passes `check` (and the container is actually an array). */
function isArrayOf<T>(v: unknown, check: (el: unknown) => el is T): v is T[] {
  return Array.isArray(v) && v.every(check);
}

function isColumn(v: unknown): v is PresentationColumn {
  return (
    isObject(v) &&
    typeof v['key'] === 'string' &&
    typeof v['label'] === 'string'
  );
}

function isDetailField(v: unknown): v is DetailField {
  return (
    isObject(v) &&
    typeof v['key'] === 'string' &&
    typeof v['label'] === 'string' &&
    'value' in v
  );
}

function isMetric(v: unknown): v is Metric {
  if (!isObject(v)) return false;
  const val = v['value'];
  return (
    typeof v['label'] === 'string' &&
    (typeof val === 'string' || typeof val === 'number')
  );
}

function isTimelineItem(v: unknown): v is TimelineItem {
  return isObject(v) && typeof v['title'] === 'string';
}

/**
 * Runtime type guard for a presentation block. Discriminates on `kind` and
 * checks the required fields per variant; unknown kinds are rejected. Extra
 * fields (e.g. the wire `messageId`) are allowed.
 */
export function isPresentationBlock(v: unknown): v is PresentationBlock {
  if (!isObject(v)) return false;
  const kind = v['kind'];
  if (typeof kind !== 'string') return false;
  // Fields common to every block.
  if (typeof v['entity'] !== 'string' || typeof v['title'] !== 'string') {
    return false;
  }

  switch (kind) {
    case 'table':
      return (
        isArrayOf(v['columns'], isColumn) &&
        isArrayOf(v['rows'], isObject) &&
        (v['omittedCount'] === undefined ||
          typeof v['omittedCount'] === 'number')
      );

    case 'detail_card':
      return isArrayOf(v['fields'], isDetailField);

    case 'metric_cards':
      return isArrayOf(v['metrics'], isMetric);

    case 'timeline':
      return isArrayOf(v['items'], isTimelineItem);

    case 'empty_state':
      return typeof v['message'] === 'string';

    default:
      return false;
  }
}
